/**
 * Voilacode platform console — companies on the platform.
 * Route: /platform
 * @file src/web/features/platform/pages/index.tsx
 *
 * @llm-rule WHEN: The super admin manages customer companies
 * @llm-rule AVOID: Adding any employee-level data to this screen — the whole
 *   privacy promise is that the platform operator sees companies, not people.
 *   The API will not serve it, and this page must not ask for it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  Skeleton,
  Switch,
  toast,
  useConfirm,
} from '@bloomneo/uikit';
import { Building2, Copy, KeyRound, MessageCircle, Plus, ShieldCheck } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { PLATFORM, PRODUCT } from '../../../shared/brand';
import { isSuperAdmin, type OrgRole } from '../../../shared/domain';
import { Panel, SectionLabel, StatTile } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';

interface OwnerAccount {
  id: string;
  name: string | null;
  email: string;
  lastSignInOn: string | null;
  isActive: boolean;
}

interface Organisation {
  id: string;
  name: string;
  slug: string;
  plan: string;
  seatLimit: number | null;
  seatsUsed: number;
  isActive: boolean;
  emailDomain: string | null;
  createdAt: string;
  lastActivityOn: string | null;
  owner: OwnerAccount | null;
}

interface PlatformStats {
  organisations: number;
  active: number;
  suspended: number;
  seats: number;
}

/** One account's credentials, as handed over at creation. */
interface IssuedAccount {
  email: string;
  password: string;
  roleLabel: string;
  title?: string;
}

const EMPTY_FORM = {
  name: '',
  slug: '',
  emailDomain: '',
  plan: 'standard',
  seatLimit: '',
  adminName: '',
  adminEmail: '',
};

function PlatformConsole() {
  const { user, logout } = useAuth();
  const { request } = useAppRequest();
  const confirm = useConfirm();

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [issued, setIssued] = useState<{
    company: string;
    accounts: IssuedAccount[];
    /** Prebuilt handover text. Absent for a single-account password reissue. */
    message?: string;
  } | null>(null);
  /** Organisation id currently having its Company Admin password reissued. */
  const [resetting, setResetting] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [listResult, statsResult] = await Promise.all([
      request<{ organisations: Organisation[] }>('/api/organisation'),
      request<{ stats: PlatformStats }>('/api/organisation/stats'),
    ]);

    if (listResult.ok) setOrganisations(listResult.data.organisations);
    else toast.error(listResult.error);

    if (statsResult.ok) setStats(statsResult.data.stats);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createCompany() {
    if (!form.name.trim() || !form.adminName.trim() || !form.adminEmail.trim()) {
      toast.error('Company name, admin name, and admin email are all required');
      return;
    }

    setBusy(true);
    try {
      const result = await request<{
        organisation: Organisation;
        admin: { email: string };
        accounts?: IssuedAccount[];
        credentialMessage?: string;
        temporaryPassword?: string;
      }>('/api/organisation', {
        method: 'POST',
        body: {
          name: form.name,
          slug: form.slug || undefined,
          emailDomain: form.emailDomain || undefined,
          plan: form.plan || undefined,
          seatLimit: form.seatLimit ? Number(form.seatLimit) : null,
          admin: { name: form.adminName, email: form.adminEmail },
        },
      });

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      toast.success(`${result.data.organisation.name} created`);
      if (result.data.accounts?.length) {
        setIssued({
          company: result.data.organisation.name,
          accounts: result.data.accounts,
          message: result.data.credentialMessage,
        });
      }
      setForm(EMPTY_FORM);
      setOpen(false);
      void load();
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      // Clipboard is blocked outside a secure context — the value is on
      // screen anyway, so this is a nicety, not a failure worth shouting about.
      toast.info(`Copy manually: ${text}`);
    }
  }

  /**
   * There is no way to read an existing password back — they are hashed.
   * Recovering a lost Company Admin sign-in means issuing a new one, shown once here.
   */
  async function resetOwnerPassword(org: Organisation) {
    if (!org.owner) return;

    const ok = await confirm({
      title: `Issue a new password for ${org.owner.email}?`,
      description:
        'Their current password stops working immediately. The new one is shown once — copy it before you close this screen.',
      confirmLabel: 'Issue new password',
    });
    if (!ok) return;

    setResetting(org.id);
    try {
      const result = await request<{ temporaryPassword: string; owner: { email: string } }>(
        `/api/organisation/${org.id}/reset-owner-password`,
        { method: 'POST' },
      );

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      setIssued({
        company: org.name,
        accounts: [
          {
            email: result.data.owner.email,
            password: result.data.temporaryPassword,
            roleLabel: 'Company Admin',
          },
        ],
      });
      toast.success('New password issued');
      void load();
    } finally {
      setResetting(null);
    }
  }

  async function toggleActive(org: Organisation) {
    const result = await request(`/api/organisation/${org.id}`, {
      method: 'PUT',
      body: { isActive: !org.isActive },
    });
    if (!result.ok) return toast.error(result.error);
    toast.success(org.isActive ? `${org.name} suspended` : `${org.name} restored`);
    void load();
  }

  // A company account must never land on the platform console.
  if (!isSuperAdmin(user?.orgRole as OrgRole)) {
    return <Navigate to="/clock" replace />;
  }

  return (
    <div className="min-h-screen bg-paper-warm">
      <header className="border-b border-rule bg-paper">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3.5">
          <div className="flex items-baseline gap-2">
            <span className="text-[17px] font-semibold tracking-tight text-ink">
              {PLATFORM.name}
            </span>
            <span className="hidden text-[11.5px] text-ink-faint sm:inline">{PLATFORM.tagline}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[12px] text-ink-faint">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => logout()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-6">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-ink">
              Companies
            </h1>
            <p className="mt-1.5 text-[13.5px] text-ink-soft">
              Each one runs {PRODUCT.name} for its own people.
            </p>
          </div>
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-1.5 size-4" />
            Add company
          </Button>
        </div>

        {/* Privacy statement — this is a promise the API enforces, so say it. */}
        <div className="mb-5 flex items-start gap-2.5 rounded-[14px] border border-dashed border-rule bg-paper px-4 py-3">
          <ShieldCheck className="mt-0.5 size-4 flex-none text-ink-faint" />
          <p className="text-[12px] leading-relaxed text-ink-soft">
            You manage companies, seats, and access. You cannot see who clocked in, what was praised
            or flagged, or anything said in chat — those belong to each company.
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-72 w-full rounded-[18px]" />
        ) : (
          <div className="flex flex-col gap-5">
            {stats && (
              <div className="flex flex-wrap gap-2.5">
                <StatTile value={stats.organisations} label="companies" />
                <StatTile value={stats.active} label="active" tone="ok" />
                <StatTile value={stats.suspended} label="suspended" tone="warn" />
                <StatTile value={stats.seats} label="seats in use" />
              </div>
            )}

            <Panel padded={false} className="overflow-hidden">
              <div className="border-b border-rule bg-paper-warm px-5 py-2.5">
                <SectionLabel>All companies</SectionLabel>
              </div>

              {organisations.length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    icon={<Building2 />}
                    title="No companies yet"
                    description="Add your first company and hand its admin the keys."
                    action={<Button onClick={() => setOpen(true)}>Add company</Button>}
                  />
                </div>
              ) : (
                <ul className="divide-y divide-rule">
                  {organisations.map((org) => (
                    <li key={org.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="text-[14px] font-medium text-ink">{org.name}</span>
                            <span className="text-[11.5px] text-ink-ghost">/{org.slug}</span>
                            {!org.isActive && (
                              <span className="rounded-full bg-[var(--state-bad-bg)] px-2 py-0.5 text-[10.5px] font-medium text-flag">
                                suspended
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[11.5px] text-ink-faint">
                            {org.plan} · {org.seatsUsed}
                            {org.seatLimit ? `/${org.seatLimit}` : ''} seat
                            {org.seatsUsed === 1 ? '' : 's'}
                            {org.emailDomain && ` · ${org.emailDomain}`}
                            {' · '}
                            {org.lastActivityOn
                              ? `last active ${org.lastActivityOn}`
                              : 'no activity yet'}
                          </div>
                        </div>

                        <label className="flex items-center gap-2 text-[12px] text-ink-faint">
                          <Switch
                            checked={org.isActive}
                            onCheckedChange={() => void toggleActive(org)}
                            aria-label={`${org.isActive ? 'Suspend' : 'Restore'} ${org.name}`}
                          />
                          {org.isActive ? 'Active' : 'Suspended'}
                        </label>
                      </div>

                      {/* Company Admin sign-in — the support handle for this company. */}
                      {org.owner && (
                        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-paper-warm px-3.5 py-2.5">
                          <KeyRound className="size-3.5 flex-none text-ink-faint" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] uppercase tracking-[0.1em] text-ink-ghost">
                              Admin sign-in
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-baseline gap-2">
                              <span className="font-mono text-[12.5px] text-ink">
                                {org.owner.email}
                              </span>
                              <span className="text-[11.5px] text-ink-faint">
                                {org.owner.name}
                                {' · '}
                                {org.owner.lastSignInOn
                                  ? `last signed in ${org.owner.lastSignInOn}`
                                  : 'never signed in'}
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-[11.5px]"
                              onClick={() => void copy(org.owner!.email, 'Email')}
                            >
                              <Copy className="mr-1 size-3" />
                              Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2.5 text-[11.5px]"
                              disabled={resetting === org.id}
                              onClick={() => void resetOwnerPassword(org)}
                            >
                              {resetting === org.id ? 'Resetting…' : 'New password'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}

        {/* Credentials handed over once */}
        {issued && (
          <div className="mt-5 rounded-[14px] border border-[var(--state-warn-border)] bg-[var(--state-warn-bg)] p-4">
            <div className="text-[13px] font-semibold text-ink">
              {issued.company} — {issued.accounts.length > 1
                ? `${issued.accounts.length} accounts, shown once`
                : 'sign-in, shown once'}
            </div>

            <div className="mt-3 flex flex-col gap-3">
              {issued.accounts.map((account) => (
                <div
                  key={account.email}
                  className="rounded-[10px] border border-line bg-paper/60 p-2.5"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                    {account.roleLabel}
                    {account.title ? (
                      <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-ghost">
                        {account.title}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-1.5 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-[74px] shrink-0 text-[11px] uppercase tracking-[0.1em] text-ink-ghost">
                        Username
                      </span>
                      <code className="flex-1 truncate rounded bg-paper px-2 py-1 font-mono text-[13px] text-ink">
                        {account.email}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 shrink-0 px-2"
                        onClick={() => void copy(account.email, 'Username')}
                      >
                        <Copy className="size-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-[74px] shrink-0 text-[11px] uppercase tracking-[0.1em] text-ink-ghost">
                        Password
                      </span>
                      <code className="flex-1 truncate rounded bg-paper px-2 py-1 font-mono text-[13px] text-ink">
                        {account.password}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 shrink-0 px-2"
                        onClick={() => void copy(account.password, 'Password')}
                      >
                        <Copy className="size-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-2.5 text-[11.5px] leading-relaxed text-ink-soft">
              These passwords are shown once and are not stored anywhere readable. If one is lost,
              issue a new one — it cannot be looked up.
            </p>

            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {issued.message && (
                <>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => void copy(issued.message!, 'Message')}
                  >
                    <Copy className="mr-1.5 size-3" />
                    Copy message
                  </Button>
                  {/* No number: wa.me/?text= opens WhatsApp with the text
                      prefilled and lets the operator pick the contact. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(
                        `https://wa.me/?text=${encodeURIComponent(issued.message!)}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  >
                    <MessageCircle className="mr-1.5 size-3" />
                    Open in WhatsApp
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => setIssued(null)}>
                I’ve saved it
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Create company */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a company</DialogTitle>
            <DialogDescription>
              Creating a company also creates its Company Admin, one Head, one Manager, and one Employee. The admin account is the only way in — you cannot
              add people inside a company yourself.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Company name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Technologies"
              />
            </FormField>

            <FormField label="Handle" helper="Defaults to the name, URL-safe">
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="acme"
              />
            </FormField>

            <FormField label="Email domain" helper="Optional">
              <Input
                value={form.emailDomain}
                onChange={(e) => setForm({ ...form, emailDomain: e.target.value })}
                placeholder="acme.com"
              />
            </FormField>

            <FormField label="Seat limit" helper="Blank for unlimited">
              <Input
                type="number"
                min={1}
                value={form.seatLimit}
                onChange={(e) => setForm({ ...form, seatLimit: e.target.value })}
                placeholder="50"
              />
            </FormField>

            <FormField label="Company Admin name" required>
              <Input
                value={form.adminName}
                onChange={(e) => setForm({ ...form, adminName: e.target.value })}
                placeholder="Rahul Verma"
              />
            </FormField>

            <FormField label="Company Admin email" required>
              <Input
                type="email"
                value={form.adminEmail}
                onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
                placeholder="rahul@acme.com"
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void createCompany()} disabled={busy}>
              {busy ? 'Creating…' : 'Create company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PlatformRoute() {
  return (
    <AuthGuard>
      <PlatformConsole />
    </AuthGuard>
  );
}
